import { HttpErrorResponse } from '@angular/common/http';

/** Known upstream messages (lower-case, without final punctuation) and their French text. */
const MESSAGES: Record<string, string> = {
  'invalid reinstall key': 'La clé de réinstallation est invalide. Demandez une nouvelle clé.',
  'reinstall key expired': 'La clé de réinstallation a expiré. Demandez une nouvelle clé.',
  'invalid verification code': 'Le code de vérification est invalide.',
  'invalid or already-used backup code': 'Ce code de récupération est invalide ou a déjà été utilisé.',
  'invalid email or code': 'L’adresse e-mail ou le code de vérification est invalide.',
  'verification challenge expired': 'La demande de vérification a expiré. Recommencez.',
  'passkey challenge expired. please try again': 'La demande de connexion a expiré. Réessayez.',
  'invalid or expired activation token': 'Le lien d’activation est invalide ou a expiré.',
  'version de réinstallation indisponible': 'Cette version de réinstallation est indisponible.',
  'une opération est déjà en cours sur ce service':
    'Une opération est déjà en cours sur ce service. Veuillez patienter.',
  'veuillez patienter avant de demander une nouvelle clé de réinstallation':
    'Veuillez patienter avant de demander une nouvelle clé de réinstallation.',
  'service not found': 'Le service demandé est introuvable.',
  'invalid otp': 'Le code de vérification est invalide.',
  'invalid code': 'Le code de vérification est invalide.',
  'otp expired': 'Le code de vérification a expiré. Demandez un nouveau code.',
  'code expired': 'Le code de vérification a expiré. Demandez un nouveau code.',
  'invalid credentials': 'Les identifiants de connexion sont incorrects.',
  unauthenticated: 'Votre session a expiré. Veuillez vous reconnecter.',
  forbidden: 'Vous ne disposez pas des autorisations nécessaires pour cette action.',
  'not found': 'La ressource demandée est introuvable.',
  'too many attempts': 'Trop de tentatives. Veuillez patienter avant de réessayer.',
  'too many requests': 'Trop de demandes. Veuillez patienter avant de réessayer.',
  'insufficient balance': 'Votre solde est insuffisant pour effectuer cette action.',
  'insufficient funds': 'Les fonds disponibles sont insuffisants.',
  'service is locked': 'Une opération est déjà en cours sur ce service. Veuillez patienter.',
  'the given data was invalid': 'Certaines informations saisies sont invalides.',
  'validation failed': 'Certaines informations saisies sont invalides.',
};

const STATUS_MESSAGES: Record<number, string> = {
  0: 'Connexion au serveur impossible. Vérifiez votre connexion et réessayez.',
  401: 'Votre authentification a échoué. Vérifiez vos identifiants et réessayez.',
  403: 'Vous ne disposez pas des autorisations nécessaires pour cette action.',
  404: 'La ressource demandée est introuvable.',
  429: 'Trop de tentatives. Veuillez patienter avant de réessayer.',
};

function statusMessage(status: number): string {
  if (Object.hasOwn(STATUS_MESSAGES, status)) return STATUS_MESSAGES[status];
  if (status >= 500) return 'Le service est temporairement indisponible. Veuillez réessayer plus tard.';
  return 'Cette action n’a pas pu aboutir. Vérifiez les informations saisies et réessayez.';
}

function validationMessage(text: string): string {
  if (/required|mandatory/.test(text)) return 'Ce champ est obligatoire.';
  if (/valid email/.test(text)) return 'Veuillez saisir une adresse e-mail valide.';
  if (/already been taken|already exists/.test(text)) return 'Cette valeur est déjà utilisée.';
  if (/confirmation.*match|does not match/.test(text)) return 'La confirmation ne correspond pas.';
  return 'La valeur saisie pour ce champ est invalide.';
}

/** Display text only: machine codes, retry delays and other response data stay intact.
 * Unknown upstream text must never leak English or infrastructure diagnostics to the UI.
 * French is the fallback language shared by all three applications.
 */
export function localizeApiError(error: HttpErrorResponse): HttpErrorResponse {
  const fallback = statusMessage(error.status);
  const translate = (value: unknown, validation = false): string => {
    const text = typeof value === 'string' ? value.trim().replace(/[.!]$/, '').toLowerCase() : '';
    if (Object.hasOwn(MESSAGES, text)) return MESSAGES[text];
    return validation ? validationMessage(text) : fallback;
  };
  const original = error.error;
  const body =
    original && typeof original === 'object' && !Array.isArray(original)
      ? { ...original, message: translate(original.message) }
      : { message: translate(original) };
  if (original?.errors && typeof original.errors === 'object' && !Array.isArray(original.errors)) {
    body.errors = Object.fromEntries(
      Object.entries(original.errors).map(([field, values]) => [
        field,
        Array.isArray(values) ? values.map((value) => translate(value, true)) : translate(values, true),
      ])
    );
  }
  const localized = new HttpErrorResponse({
    error: body,
    headers: error.headers,
    status: error.status,
    url: error.url ?? undefined,
    redirected: error.redirected,
  });
  // Some consumers display HttpErrorResponse.message rather than its JSON body.
  Object.defineProperty(localized, 'message', { value: body.message });
  return localized;
}
